// The works the commentary leans on, in one list. Like a chapter, it is the
// published page that the admin edits in place.
import type { BibliographyEntryInput } from "@bibleunleashed/shared";
import { useCallback, useEffect, useState } from "react";
import { BottomBar } from "@/components/BottomBar";
import { Field } from "@/components/Field";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { Icon } from "@/components/Icon";
import { useUser } from "@/lib/auth";
import { formatBibliographyEntry, useBibliography, useSaveBibliography } from "@/lib/bibliography";

export function Bibliography() {
  const { data: user } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: entries, isLoading, error } = useBibliography();
  const save = useSaveBibliography();
  const [draft, setDraft] = useState<BibliographyEntryInput[] | null>(null);

  useEffect(() => {
    document.title = "Bibliography · The Bible Unleashed";
  }, []);

  const patch = useCallback(
    (index: number, change: Partial<BibliographyEntryInput>) =>
      setDraft((d) => (d ? d.map((e, i) => (i === index ? { ...e, ...change } : e)) : d)),
    [],
  );

  const start = () =>
    setDraft(
      (entries ?? []).map((e) => ({
        id: e.id,
        author: e.author,
        title: e.title,
        extra: e.extra,
        url: e.url,
        year: e.year,
      })),
    );

  const doSave = async () => {
    if (!draft) return;
    await save.mutateAsync({ entries: draft });
    setDraft(null);
  };

  const list = draft ?? entries ?? [];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[760px] px-6 py-16 pb-28">
        <PageText id="bibliography" titleClassName="font-serif text-[38px] font-semibold tracking-[-0.015em]" bodyClassName="mt-3 font-serif text-lg" />

        {isLoading && <p className="mt-10 font-serif text-ink-3">Loading…</p>}
        {error && <p className="mt-10 font-serif text-ink-2">Couldn’t load the list: {error.message}</p>}
        {!isLoading && !error && list.length === 0 && <p className="mt-10 font-serif text-ink-3">Nothing here yet.</p>}

        <ul className="mt-10 space-y-4">
          {list.map((e, i) => (
            <li key={e.id} id={e.id} className="scroll-mt-20 border-b border-rule pb-4 last:border-0">
              {draft ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label="Author" value={e.author} onChange={(author) => patch(i, { author })} />
                  <Field label="Title" value={e.title} onChange={(title) => patch(i, { title })} />
                  <Field label="Publication" value={e.extra ?? ""} onChange={(extra) => patch(i, { extra: extra || null })} />
                  <Field
                    label="Year"
                    value={e.year === null ? "" : String(e.year)}
                    onChange={(year) => patch(i, { year: /^\d{1,4}$/.test(year) ? Number(year) : null })}
                  />
                  <Field label="Link" value={e.url ?? ""} onChange={(url) => patch(i, { url: url.trim() || null })} />
                  <div className="flex items-end justify-end gap-1">
                    <span className="mr-auto self-end font-sans text-[11px] text-ink-3">{e.id}</span>
                    <button
                      onClick={() => setDraft((d) => (d ? d.filter((_, j) => j !== i) : d))}
                      aria-label={`Delete ${e.title || e.id}`}
                      className="grid size-7 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink"
                    >
                      <Icon name="trash" width="15" height="15" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-serif text-[17px] leading-relaxed text-ink">{formatBibliographyEntry(e)}</p>
                  {e.url && (
                    <a href={e.url} target="_blank" rel="noopener noreferrer" className="mt-1 block truncate font-sans text-[12px] text-accent">
                      {e.url}
                    </a>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>

        {draft && (
          <button
            onClick={() =>
              setDraft((d) => [
                ...(d ?? []),
                { id: `entry-${Date.now().toString(36)}`, author: "", title: "", extra: null, url: null, year: null },
              ])
            }
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-rule py-3 text-[13px] text-ink-2 hover:border-ink-3 hover:text-ink"
          >
            <Icon name="plus" width="15" height="15" /> Add an entry
          </button>
        )}
      </main>

      {isAdmin && (
        <BottomBar>
          <div className="mx-auto flex max-w-[760px] items-center gap-2 px-6 py-2.5">
            <span className="font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
              {list.length} {list.length === 1 ? "entry" : "entries"}
            </span>
            {save.error && <span className="text-[12px] text-accent">{save.error.message}</span>}
            <span className="ml-auto flex gap-2">
              {draft ? (
                <>
                  <button onClick={() => setDraft(null)} className="rounded-md border border-rule px-3 py-1.5 text-[13px] text-ink-2 hover:text-ink">
                    Done
                  </button>
                  <button
                    onClick={() => void doSave()}
                    disabled={save.isPending}
                    className="rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
                  >
                    {save.isPending ? "Saving…" : "Save"}
                  </button>
                </>
              ) : (
                <button onClick={start} className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white">
                  <Icon name="pencil" width="15" height="15" /> Edit
                </button>
              )}
            </span>
          </div>
        </BottomBar>
      )}
    </div>
  );
}
